/**
 * @file JS - Controller - CommonLayerController.js
 * @author : Adam Lemire
 * @since 2020/11/05
 * @version 1.0
 * @summary Dernières modifications : 2021/08/25
 */

import RasterLayer from '../../classes/Layer/RasterLayer.js'; // eslint-disable-line no-unused-vars
import VectorLayer from '../../classes/Layer/VectorLayer.js'; // eslint-disable-line no-unused-vars
import { ContextMenu, LayerProperties, LayerContextMenuOptions } from '../../helpers/Selectors.js';
import { dictionary } from '../../helpers/languageDictionary.js';
import { hideContextMenus } from '../../helpers/GlobalFunctions.js';
import { gisSettings } from '../../gisSettings.js';
import { Evenements } from '../../events/Evenements.js';

const OPTION_LIBELLÉ = gisSettings.options.layer.coucheLabel.active;

/**
 * @classdesc Controller CommonLayerController, de type Template pour les sous-types de LayerController
 * @abstract
 */
export default class LayerController {
  constructor () {
    if (this.constructor === LayerController) { throw new Error('Ne peut instancier une classe abstraite'); }

    /** @type { VectorLayer | RasterLayer } */
    this._layer = undefined;
  }

  get layer () { return this._layer; }

  /**
   * @abstract
   * @param { VectorLayer | RasterLayer } layer
   */
  set layer (layer) { throw new Error('Cette méthode doit être implémentée par la sous-classe!'); }

  /*
   * LAYER PROPERTIES
   */

  updateLayerPropertiesView () {
    this._updateLayerPropertiesListeners();
    this._updateLayerPropertiesHeaderView();
    this._updateLayerPropertiesSourceNameView();
    this._updateLayerPropertiesLayerNameView();
    this._updateLayerPropertiesStyleView();
  }

  /** @abstract */
  _updateLayerPropertiesListeners () { throw new Error('Cette méthode doit être implémentée par la sous-classe!'); }

  _updateLayerPropertiesHeaderView () {
    const title = this._layer.name + (window.localStorage.getItem('lang') === 'en' ? dictionary.properties.header.en : dictionary.properties.header.fr);
    $(LayerProperties.HEADER).text(title);
  }

  _updateLayerPropertiesSourceNameView () {
    $(LayerProperties.SOURCE_NAME).text(this._layer.sourceName);
  }

  _updateLayerPropertiesLayerNameView () {
    const name = this._layer.name;
    $(LayerProperties.LAYER_NAME).val(name);
  }

  /** @abstract */
  _updateLayerPropertiesStyleView () { throw new Error('Cette méthode doit être implémentée par la sous-classe!'); }

  /*
   * APPLY PROPERTIES
   */

  _modifierPropriétés () {
    this._modifierNom();
    this._modifierStyle();
    $(LayerProperties.SECTION).addClass('hidden');
    Evenements.doitEnregistrer.notify();
  }

  _modifierNom () {
    if ($(LayerProperties.LAYER_NAME).val() !== this._layer.name) {
      this._layer.name = $(LayerProperties.LAYER_NAME).val();
    }
  }

  /** @abstract */
  _modifierStyle () { throw new Error('Cette méthode doit être implémentée par la sous-classe!'); }

  écouterNomLayerModifié () {
    const _this = this;

    this._layer.écouterNomModifié(function (newName) {
      const layerNameDiv = '.map-layers__text-' + _this._layer.id;
      $(layerNameDiv).text(_this._layer.name);
    });
  }

  /*
   * LEFT-MENU : MAP_LAYERS
   */

  //! rendre abstract et utiliser afficherÉtiquettes dans VectorLayerController
  /**
   * @param { VectorLayer | RasterLayer } layer
   * @returns { boolean }
   * */
  async _ajouterCoucheDansMenuListeCouches (layer) {
    const mLayer = await layer;
    const htmlLayerDivPret = await this._créerDivCoucheDansListeCouches(mLayer);
    console.log('ajout couche ds liste menu : ' + await mLayer.name);
    
    this.écouterCheckboxAfficherLayer(mLayer, OPTION_LIBELLÉ, htmlLayerDivPret);
    if (OPTION_LIBELLÉ) {
      this._écouterCheckboxAfficherÉtiquettes(mLayer, htmlLayerDivPret);
    }
    this._écouterMenuContextuel(mLayer, htmlLayerDivPret);
    this._écouterSélectionLayer(mLayer, htmlLayerDivPret);

    return htmlLayerDivPret;
  }

  /**
   * @abstract
   * @param { Layer } layer
   */
  _créerDivCoucheDansListeCouches (layer) { throw new Error('Cette méthode doit être implémentée par la sous-classe!'); }

  /**
   * @param { Layer } layer
   * @param { booelan } avecLibellé
   */
  async écouterCheckboxAfficherLayer (layer, avecLibellé) {
    const _this = this;
    const layerId = layer.id;
    const olLayer = await layer.olLayer;
    const olLayerLibellés = await layer.olLayerLibellés;
    const checkBox = '.map-layers__checkbox-' + layerId;
    const checkBoxLibellé = '.map-layers__label-checkbox-' + layerId;
    $(checkBox).data('layerId', layerId);
    $(checkBox).off('change').on('change', async function () {
      if (this.checked) {
        const setVisible = await olLayer.setVisible(true);
        if (olLayerLibellés && $(checkBoxLibellé).is(':checked')) olLayerLibellés.setVisible(true);
        layer.isVisible = true;
        const paramShowLayerListener = { visible: true, layerId: layerId, layerPrete: setVisible };
        _this._notifierAffichageLayerModifié(paramShowLayerListener);
      } else {
        const setVisible = await olLayer.setVisible(false);
        if (olLayerLibellés && $(checkBoxLibellé).is(':checked')) olLayerLibellés.setVisible(false);
        layer.isVisible = false;
        const paramShowLayerListener = { visible: false, layerId: layerId, layerPrete: setVisible };
        _this._notifierAffichageLayerModifié(paramShowLayerListener);
      }
    });
    if (!layer.isVisible) {
      const setVisible = await olLayer.setVisible(false);
      if (olLayerLibellés && $(checkBoxLibellé).is(':checked')) await olLayerLibellés.setVisible(false);
      $(checkBox).checked = false;
      const paramShowLayerListener = { visible: false, layerId: layerId, layerPrete: setVisible };
      this._notifierAffichageLayerModifié(paramShowLayerListener);
    }
  }

  /** @param { Layer } layer */
  async _écouterCheckboxAfficherÉtiquettes (layer) {
    if (layer instanceof VectorLayer && await layer.getLibellésExistent()) {
      const _this = this;
      const layerId = layer.id;
      const olLayerLibellés = await layer.olLayerLibellés;
      const checkBox = '.map-layers__label-checkbox-' + layerId;
      $(checkBox).data('layerId', layerId);
      $(checkBox).on('change', async function () {
        if (this.checked) {
          const setVisible = await olLayerLibellés.setVisible(true);
          layer.layerLibellésVisible = true;
          layer.updateStyle();
          const paramShowLayerListener = { visible: true, layerId: layerId, layerPrete: setVisible };
          _this._notifierAffichageLibelléModifié(paramShowLayerListener);
        } else {
          const setVisible = await olLayerLibellés.setVisible(false);
          layer.layerLibellésVisible = false;
          layer.updateStyle();
          const paramShowLayerListener = { visible: false, layerId: layerId, layerPrete: setVisible };
          _this._notifierAffichageLibelléModifié(paramShowLayerListener);
        }
      });
      if (!layer.isVisible || !layer.layerLibellésVisible) {
        const setVisible = await olLayerLibellés.setVisible(false);
        $(checkBox).prop('checked', false);
        const paramShowLayerListener = { visible: false, layerId: layerId, layerPrete: setVisible };
        layer.updateStyle();
        this._notifierAffichageLibelléModifié(paramShowLayerListener);
      }
    }
  }

  /** @param { Layer } layer */
  _écouterMenuContextuel (layer) {
    const layerId = layer.id;
    const menu = document.querySelector(ContextMenu.MAP_LAYERS);
    const div = 'map-layers__layer-' + layerId;
    const idDiv = '#' + div;

    const setPosition = ({
      top,
      left
    }) => {
      hideContextMenus();
      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
      menu.style.display = 'block';

      $(ContextMenu.MAP_LAYERS).removeClass('hidden');

      if ($(idDiv).hasClass('map-layers__layer--vector-data')) {
        $(LayerContextMenuOptions.ATTRIBUTES_TABLE).show();
        $(LayerContextMenuOptions.ATTRIBUTES_TABLE).next('.context-menu__option--separator').show();
      } else {
        $(LayerContextMenuOptions.ATTRIBUTES_TABLE).hide();
        $(LayerContextMenuOptions.ATTRIBUTES_TABLE).next('.context-menu__option--separator').hide();
      }
    };

    const contextHandler = (e) => {
      e.preventDefault();
      const origin = {
        left: e.pageX,
        top: e.pageY
      };
      setPosition(origin);
      $(ContextMenu.MAP_LAYERS).data('layerId', layerId);
      return false;
    };

    document.getElementById(div).removeEventListener('contextmenu', contextHandler);
    document.getElementById(div).addEventListener('contextmenu', contextHandler);
  }

  /** @param { Layer } layer */
  _écouterSélectionLayer (layer) {
    const _this = this;
    const layerId = layer.id;
    const idMapLayerMenu = '#map-layers__layer-' + layerId;

    $(idMapLayerMenu).on('click tap', function () {
      _this._notifierLayerSelectionnée(layerId);
    });
  }

  _notifierLayerSelectionnée (layerId) {}

  écouterLayerSelectionnée (listener) {
    this._notifierLayerSelectionnée = listener;
  }

  /**
   * @param { object } param
   * @param { boolean } param.visible
   * @param { number } param.layerId
   * @param { boolean } param.layerPrete
   */
  _notifierAffichageLayerModifié (param) {}

  registerShowLayerListener (listener) {
    this._notifierAffichageLayerModifié = listener;
  }

  _notifierAffichageLibelléModifié (param) {}

  écouterAffichageLibelléModifié (listener) {
    this._notifierAffichageLibelléModifié = listener;
  }
}
